/* eslint-disable react-refresh/only-export-components */
import {
  DimeSeparator,
  DimeSidebar,
  DimeSidebarContent,
  DimeSidebarGroup,
  DimeSidebarGroupContent,
  DimeSidebarInset,
  DimeSidebarMenu,
  DimeSidebarMenuButton,
  DimeSidebarMenuItem,
  DimeSidebarProvider,
  DimeSidebarTrigger,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/sidebar')({
  component: SidebarDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

const NAV_ITEMS = [
  { label: 'Dashboard', href: '#' },
  { label: 'Users', href: '#' },
  { label: 'Inventory', href: '#' },
  { label: 'Accounting', href: '#' },
] as const;

function SidebarDemo({ defaultOpen }: Readonly<{ defaultOpen: boolean }>) {
  return (
    <div className="relative h-80 overflow-hidden rounded-lg border border-border bg-background">
      <DimeSidebarProvider defaultOpen={defaultOpen} className="min-h-0! h-full">
        <DimeSidebar collapsible="icon" className="absolute">
          <DimeSidebarContent>
            <DimeSidebarGroup>
              <DimeSidebarGroupContent>
                <DimeSidebarMenu>
                  {NAV_ITEMS.map((item) => (
                    <DimeSidebarMenuItem key={item.label}>
                      <DimeSidebarMenuButton asChild tooltip={item.label}>
                        <a href={item.href} onClick={(ev) => ev.preventDefault()}>
                          <span
                            aria-hidden
                            className="flex size-4 items-center justify-center text-[10px] font-semibold"
                          >
                            {item.label.charAt(0)}
                          </span>
                          <span>{item.label}</span>
                        </a>
                      </DimeSidebarMenuButton>
                    </DimeSidebarMenuItem>
                  ))}
                </DimeSidebarMenu>
              </DimeSidebarGroupContent>
            </DimeSidebarGroup>
          </DimeSidebarContent>
        </DimeSidebar>
        <DimeSidebarInset className="min-h-0">
          <div className="flex items-center gap-2 border-b border-border p-2">
            <DimeSidebarTrigger />
            <span className="text-sm text-muted-foreground">Main content area</span>
          </div>
        </DimeSidebarInset>
      </DimeSidebarProvider>
    </div>
  );
}

function SidebarDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Sidebar</h1>
      <p className="mb-8 text-muted-foreground">
        shadcn Sidebar wrapped as <code className="font-mono text-sm">DimeSidebar*</code>. Compose{' '}
        <code className="font-mono text-sm">DimeSidebarProvider</code> →{' '}
        <code className="font-mono text-sm">DimeSidebar</code> →{' '}
        <code className="font-mono text-sm">DimeSidebarInset</code>. Desktop width is{' '}
        <code className="font-mono text-sm">16rem</code> with{' '}
        <code className="font-mono text-sm">collapsible=&quot;icon&quot;</code>.
      </p>

      <DocsSection title="Expanded (open)">
        <SidebarDemo defaultOpen />
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeSidebarTrigger</code> (or a custom header control with{' '}
          <code className="font-mono">useDimeSidebar</code>) to toggle.
        </p>
      </DocsSection>

      <DocsSection title="Collapsed (closed)">
        <SidebarDemo defaultOpen={false} />
        <p className="mt-3 text-sm text-muted-foreground">
          Icon mode keeps items mounted with tooltips. Pass{' '}
          <code className="font-mono">tooltip</code> on{' '}
          <code className="font-mono">DimeSidebarMenuButton</code>.
        </p>
      </DocsSection>

      <DocsSection title="Usage pattern">
        <div className="rounded-lg border border-border bg-muted/40 p-4 font-mono text-xs leading-relaxed text-foreground">
          <p>{`<DimeSidebarProvider>`}</p>
          <p className="pl-4">{`<DimeSidebar collapsible="icon">…</DimeSidebar>`}</p>
          <p className="pl-4">{`<DimeSidebarInset>`}</p>
          <p className="pl-8">{`<DimeSidebarTrigger />`}</p>
          <p className="pl-8">{`{children}`}</p>
          <p className="pl-4">{`</DimeSidebarInset>`}</p>
          <p>{`</DimeSidebarProvider>`}</p>
        </div>
      </DocsSection>
    </div>
  );
}
